/**
 * @module user
 */
import { Record } from 'immutable';
import { apiPost } from './api';
import { authUrl, userServiceUrl } from './const';

const { location } = window;

/**
 * @name check
 * @desc Check if a user is authorized.
 * @param redirect
 * @returns {Promise<User>}
 * @throws {UnauthorizedApiError}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function check(redirect = true) {
    const res = await apiPost(`${userServiceUrl}/check`, null, {
        redirect,
    });
    return new User(res);
}

/**
 * @name login
 * @desc Authorize the user on the backend.
 * @param {{login:string,password:string}} param0
 * @returns {Promise<User>}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function login({ login, password }) {
    const res = await apiPost(`${userServiceUrl}/login`, { login, password });
    return new User(res);
}

/**
 * @name logout
 * @desc Unauthorize a user on the backend.
 * @returns {Promise<{}>}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function logout(redirect = true) {
    await apiPost(`${userServiceUrl}/logout`, null);
    if (redirect) {
        location.assign(authUrl);
    }
}

/**
 * @name register
 * @desc Register a user on the backend.
 * @param {{login:string,password:string,firstName:string,lastName:string}} param0
 * @returns {Promise<User>}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function register({
    login,
    password,
    firstName,
    lastName,
    termsAccepted,
    allowNewsletters,
    origin,
}) {
    const res = await apiPost(`${userServiceUrl}/register`, {
        login,
        password,
        firstName,
        lastName,
        termsAccepted,
        allowNewsletters,
        origin,
    });
    return new User(res);
}

/**
 * @name startReset
 * @desc Starts reset password action.
 * @param {{login:string}} param0
 * @returns {Promise}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function startReset({ login }) {
    const res = await apiPost(`${userServiceUrl}/start-reset`, {
        login,
    });
    return res;
}

/**
 * @name reset
 * @desc Resets password.
 * @param {{id:string,token:string,password:string,autoLogin:boolean}} param0
 * @returns {Promise}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function reset({
    id,
    token,
    password,
    autoLogin = false,
    termsAccepted,
}) {
    const res = await apiPost(`${userServiceUrl}/reset`, {
        id,
        token,
        password,
        autoLogin,
        termsAccepted,
    });
    return res;
}

/**
 * @name confirmEmail
 * @desc Confirms users email.
 * @param {{id:string,token:string}} param0
 * @returns {Promise}
 * @throws {GeneralApiError}
 * @async
 * @public
 */
export async function confirmEmail({ id, token }) {
    const res = await apiPost(`${userServiceUrl}/confirm-email`, {
        id,
        token,
    });
    return res;
}

export const USER_ORIGIN_BACKUP_NINJA = 'bn';
export const USER_ORIGIN_CONFIG_FILES_MANAGEMENT = 'cm';
export const USER_ORIGIN_VENDOR = 's9s-vendor';
export const USER_ORIGIN_VENDOR_EXOSCALE = 'exoscale';

export const USER_STATUS_ACTIVE = 0;
export const USER_STATUS_SUSPENDED = 1;

/**
 * @class User
 * @classdesc User model.
 */
export class User extends Record({
    id: null,
    login: null,
    firstName: null,
    lastName: null,
    status: null,
    emailConfirmed: null,
    subscription: null,
    pendingRemoval: null,
    allowNewsletters: null,
    termsAccepted: null,
    origin: null,
    tasks: [],
}) {
    isOriginBackupNinja() {
        return this.origin === USER_ORIGIN_BACKUP_NINJA;
    }

    isOriginConfigFilesManagement() {
        return this.origin === USER_ORIGIN_CONFIG_FILES_MANAGEMENT;
    }

    isOriginVendor() {
        return this.origin === USER_ORIGIN_VENDOR;
    }

    isOriginVendorExoscale() {
        return this.origin === USER_ORIGIN_VENDOR_EXOSCALE;
    }

    isActive() {
        return this.status === USER_STATUS_ACTIVE;
    }

    isSuspended() {
        return this.status === USER_STATUS_SUSPENDED;
    }
}
